<script setup lang="ts">
import { computed, ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { capitalize } from 'lodash';
import { guideList, tourTypeList } from '@/constants';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { getTextVariant } from '@/utils/stringUtils';
import { useViewComposable } from '@/views/composable';
import InputText from 'primevue/inputtext';
import Fluid from 'primevue/fluid';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';

interface Props {
  inputValue: object;
  validate: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{ 'update:inputValue': [value: any] }>();

const isLoading = ref(false);
const groupTypeList = ref([]);
const groupTypeManagement = useGroupTypeStore();
const { apiOrder } = useViewComposable();
const currentLanguage = getCurrentLocale();
const langCapitalize = capitalize(currentLanguage);

const inputValue = computed({
  get() {
    return props.inputValue;
  },
  set(value) {
    emits('update:inputValue', value);
  }
});

const handleReplaceNameToValue = (value: string, field: string) => {
  if (field === 'tourType') {
    return tourTypeList.find((tourType: any) => tourType[`name${langCapitalize}`] === value)?.value;
  }
  return guideList.find((guide: any) => guide[`name${langCapitalize}`] === value)?.value;
};

const getTourTypeGuide = () => {
  inputValue.value.tourType = handleReplaceNameToValue(inputValue.value.tourType, 'tourType');
  inputValue.value.guided = handleReplaceNameToValue(inputValue.value.guided, 'guided');
  return inputValue.value;
};
getTourTypeGuide();

const getGroupTypeData = async () => {
  isLoading.value = true;
  groupTypeManagement
    .getGroupTypeList()
    .then((response) => {
      groupTypeList.value = response.data;
    })
    .finally(() => (isLoading.value = false));
};
getGroupTypeData();
</script>
<template>
  <div class="tour-info-group">
    <Fluid class="mb-3">
      <div class="flex grid">
        <div class="col-6">
          <label for="tour_type_en" class="font-title">{{ $t('management.tour.tour_type_en') }} <span class="text-primary">*</span></label>
          <Select
            id="tour_type_en"
            v-model="inputValue.tourType"
            :options="tourTypeList"
            :placeholder="$t('advance_quote.form.select_group_type')"
            optionLabel="nameEn"
            optionValue="value"
            :class="validate.tourType?.$error ? 'p-invalid w-full' : 'w-full'"
          >
            <template #option="slotProps">
              <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="slotProps.option['nameEn']">
                {{ slotProps.option['nameEn'] }}
              </div>
            </template>
          </Select>
          <p class="text-red-500 pt-1" v-if="validate.tourType?.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-6">
          <label for="tour_type_fr" class="font-title">{{ $t('management.tour.tour_type_fr') }} <span class="text-primary">*</span></label>
          <Select
            id="tour_type_fr"
            v-model="inputValue.tourType"
            :options="tourTypeList"
            :placeholder="$t('advance_quote.form.select_group_type')"
            optionLabel="nameFr"
            optionValue="value"
            :class="validate.tourType?.$error ? 'p-invalid w-full' : 'w-full'"
          >
            <template #option="slotProps">
              <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="slotProps.option['nameFr']">
                {{ slotProps.option['nameFr'] }}
              </div>
            </template>
          </Select>
          <p class="text-red-500 pt-1" v-if="validate.tourType?.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>

    <Fluid class="mb-2">
      <div class="flex grid">
        <div class="col-6">
          <label for="guide_en" class="font-title">{{ $t('management.tour.guide_en') }} <span class="text-primary">*</span></label>
          <Select id="guide_en" v-model="inputValue.guided" :options="guideList" :placeholder="$t('advance_quote.form.select_group_type')" optionLabel="nameEn" optionValue="value" :class="validate.guided?.$error ? 'p-invalid w-full' : 'w-full'">
            <template #option="slotProps">
              <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="slotProps.option['nameEn']">
                {{ slotProps.option['nameEn'] }}
              </div>
            </template>
          </Select>
          <p class="text-red-500 pt-1" v-if="validate.guided?.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-6">
          <label for="guide_fr" class="font-title">{{ $t('management.tour.guide_fr') }} <span class="text-primary">*</span></label>
          <Select id="guide_fr" v-model="inputValue.guided" :options="guideList" :placeholder="$t('advance_quote.form.select_group_type')" optionLabel="nameFr" optionValue="value" :class="validate.guided?.$error ? 'p-invalid w-full' : 'w-full'">
            <template #option="slotProps">
              <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="slotProps.option['nameFr']">
                {{ slotProps.option['nameFr'] }}
              </div>
            </template>
          </Select>
          <p class="text-red-500 pt-1" v-if="validate.guided?.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>

    <Fluid class="mb-3">
      <div class="flex grid">
        <div class="col-6">
          <label for="tour_duration_en" class="font-title">{{ $t('management.tour.tour_duration_en') }} <span class="text-primary">*</span></label>
          <InputText id="tour_duration_en" :class="validate.tourDurationEn?.$error ? 'p-invalid w-full' : 'w-full'" v-model="inputValue.tourDurationEn" />
          <p class="text-red-500 pt-1" v-if="validate.tourDurationEn?.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-6">
          <label for="tour_duration_fr" class="font-title">{{ $t('management.tour.tour_duration_fr') }} <span class="text-primary">*</span></label>
          <InputText id="tour_duration_fr" :class="validate.tourDurationFr?.$error ? 'p-invalid w-full' : 'w-full'" v-model="inputValue.tourDurationFr" />
          <p class="text-red-500 pt-1" v-if="validate.tourDurationFr?.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>

    <Fluid class="mb-3">
      <div class="flex grid">
        <div class="col-6">
          <label for="destination_code" class="font-title">{{ $t('management.package.destination_code') }} </label>
          <InputText id="destination_code" class="w-full" v-model="inputValue.destinationCode" />
        </div>
        <div class="col-6">
          <label for="maximum_rooms" class="font-title">{{ $t('management.hotel.maximum_rooms') }} <span class="text-primary">*</span></label>
          <InputText id="maximum_rooms" :class="validate.maxRooms?.$error ? 'p-invalid w-full' : 'w-full'" v-model="inputValue.maxRooms" />
          <p class="text-red-500 pt-1" v-if="validate.maxRooms?.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>

    <Fluid class="mb-1">
      <div class="flex grid">
        <div class="col-6">
          <label for="group_type_select" class="font-title">{{ $t('common.labels.group_type') }} <span class="text-primary">*</span></label>
          <MultiSelect
            id="group_type_select"
            v-model="inputValue.groupTypes"
            display="chip"
            :maxSelectedLabels="1"
            :options="groupTypeList"
            :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
            optionValue="sk"
            :placeholder="$t('advance_quote.form.select_group_type')"
            :class="validate.groupTypes?.$error ? 'p-invalid w-full' : 'w-full'"
          >
            <template #option="slotProps">
              <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="getTextVariant(currentLanguage, slotProps.option['name'], slotProps.option['nameFr'])">
                {{ getTextVariant(currentLanguage, slotProps.option['name'], slotProps.option['nameFr']) }}
              </div>
            </template>
          </MultiSelect>
          <p class="text-red-500 pt-1" v-if="validate.groupTypes?.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-6">
          <label for="tour_api_call_order" class="font-title">{{ $t('management.tour.tour_api_call_order') }} </label>
          <Select id="tour_api_call_order" :options="apiOrder" optionLabel="label" optionValue="value" v-model="inputValue.tourApiCallOrder">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <div class="overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type: any) => type.value === slotProps.value)?.label }}</div>
              </div>
            </template>
          </Select>
        </div>
      </div>
    </Fluid>

    <Fluid>
      <div class="flex grid">
        <div class="col-12">
          <label for="image_url" class="font-title">{{ $t('management.tour.image') }} </label>
          <InputText id="image_url" class="w-full" v-model="inputValue.imageLink" />
        </div>
      </div>
    </Fluid>
  </div>
</template>
